import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import { differenceInUtcDays, toEndOfUtcDay, toStartOfUtcDay } from '@chainflip/utils/date';
import { isTruthy } from '@chainflip/utils/guard';
import { type UTCDate } from '@date-fns/utc';
import { type ResolutionString } from '@tradingview/chart';
import BigNumber from 'bignumber.js';
import { subDays } from 'date-fns';
import Toggle from '@/shared/components/atoms/Toggle';
import Button from '@/shared/components/flip-ui-kit/Button';
import AssetPoolLogo from '@/shared/components/logo/AssetPool';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import { TabPanel } from '@/shared/components/molecules/TabPanel';
import OrderFillsTable from '@/shared/components/orders/OrderFillsTable';
import RpcOrdersTable from '@/shared/components/orders/RpcOrdersTable';
import { PeriodSelector } from '@/shared/components/PeriodSelector';
import { TVChartContainer } from '@/shared/components/TVChartContainer';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { getCachePoolsQuery, getCachePoolsWithOrdersQuery } from '@/shared/graphql/cache/pools';
import { getAllSwapsQuery } from '@/shared/graphql/reporting/swaps';
import useChainflipAssetPrices from '@/shared/hooks/useChainflipAssetPrices';
import { useComparePoolPrice } from '@/shared/hooks/useComparePoolPrice';
import { useCurrentDate } from '@/shared/hooks/useCurrentDate';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import useLocalStorage from '@/shared/hooks/useLocalStorage';
import useRemotePagination from '@/shared/hooks/useRemotePagination';
import useSupportedAssets from '@/shared/hooks/useSupportedAssets';
import useTracking from '@/shared/hooks/useTracking';
import ArrowIcon from '@/shared/icons/flip-ui-kit/small/Arrow';
import { getTimeframeOrFormattedDateLabels, localDayToUTCDay } from '@/shared/utils/date';
import { chainflipAssetMap } from '@/shared/utils/env';
import { parseAsset } from '@/shared/utils/helpers';
import { addValueToRpcOrders, transformCacheOrdersToRpcOrders } from '@/shared/utils/orders';
import { type OpenOrdersCacheResult } from '@/shared/utils/schemas';
import { Layout } from '../../components/Layout';
import { PoolCards } from '../../components/PoolsPage/PoolCards';
import { PoolSwapRow } from '../../components/PoolsPage/PoolSwapRow';
import { BlockExplorerEvents, type BlockExplorerTrackEvents } from '../../types/track';

export default function Asset() {
  const router = useRouter();
  const baseAsset = parseAsset(router.query.asset, { type: 'base' });

  const track = useTracking<BlockExplorerTrackEvents>();

  const [startDate, setStartDate] = useState(() =>
    toStartOfUtcDay(localDayToUTCDay(subDays(new Date(), 29))),
  );
  const [endDate, setEndDate] = useState(() => toEndOfUtcDay(localDayToUTCDay(new Date())));
  const [selectedDates, setSelectedDates] = useState([startDate, endDate]);

  const [hideInternalSwaps, setHideInternalSwaps] = useLocalStorage('hideInternalSwaps', (value) =>
    typeof value === 'boolean' ? value : true,
  );
  const [hideSmallOrderFillAmounts, setHideSmallOrderFillAmounts] = useLocalStorage(
    'hideSmallOrderFillAmounts',
    (value) => (typeof value === 'boolean' ? value : true),
  );

  const {
    data: swapData,
    isLoading: swapLoading,
    paginationControls: swapPagination,
  } = useRemotePagination(getAllSwapsQuery, {
    variables: {
      asset: baseAsset,
      includeInternals: !hideInternalSwaps,
    },
    context: {
      clientName: 'reportingService',
    },
    useQueryParam: false,
  });

  const currDate = useCurrentDate(10_000);
  const {
    latestPrice,
    isLoading: marketDataLoading,
    diff,
    percentDiff,
  } = useComparePoolPrice({
    baseAsset,
    startDate,
    endDate: currDate,
  });

  const preciseUsdPrice = new BigNumber(latestPrice ?? 0).toFormat(4, BigNumber.ROUND_DOWN);

  useEffect(() => {
    if (swapPagination.pageCounter.current !== 1) swapPagination.pageCounter.set(1);
  }, [hideInternalSwaps]);

  const { data: cacheData, isLoading: cacheDataLoading } = useGqlQuery(
    getCachePoolsWithOrdersQuery,
    {
      variables: { baseAsset },
      context: { clientName: 'statechainCache' },
    },
  );

  const swapRows = useMemo(
    () =>
      swapData?.allSwapRequests?.edges.map((edge) => (
        <PoolSwapRow key={edge.node.id} swapRequest={edge.node} asset={baseAsset} />
      )) || [],
    [swapData],
  );

  const rpcOrders = transformCacheOrdersToRpcOrders(
    [
      cacheData?.pools?.nodes[0]?.openOrders &&
        ({
          baseAsset,
          quoteAsset: 'Usdc',
          orders: cacheData?.pools?.nodes[0]?.openOrders,
        } as OpenOrdersCacheResult),
    ].filter(isTruthy),
  );

  const { data: poolData, isLoading: poolDataLoading } = useGqlQuery(getCachePoolsQuery, {
    context: { clientName: 'statechainCache' },
    refetchInterval: TABLE_POLL_INTERVAL,
  });
  const { prices: assetPrices, isLoading: assetPricesLoading } = useChainflipAssetPrices();
  const openOrders = addValueToRpcOrders({
    rpcOrders,
    poolData: poolData?.pools?.nodes,
    assetPrices,
  });

  const utcDaysDifference = useMemo(
    () => differenceInUtcDays({ start: startDate, end: currDate }) + 1,
    [startDate, currDate],
  );

  const baseChainflipAssets = useSupportedAssets('base');

  return (
    <Layout
      title={`${chainflipAssetMap[baseAsset].symbol} (${chainflipAssetMap[baseAsset].chain.name}) Pool`}
      resourceNotFound={!baseChainflipAssets.includes(baseAsset)}
      breadcrumbs={[
        {
          text: 'Home',
          href: '/',
        },
        {
          text: 'Pools',
          href: '/pools',
        },
        {
          text: `${chainflipAssetMap[baseAsset].symbol}/${chainflipAssetMap.Usdc.symbol}`,
        },
      ]}
    >
      <div className="w-full space-y-6">
        <div className="flex flex-col justify-between gap-y-6 md:flex-row md:items-center">
          <div className="flex flex-row items-center space-x-4">
            <AssetPoolLogo asset={baseAsset} size="large" />
            <div className="text-31 text-cf-white">
              {chainflipAssetMap[baseAsset].symbol}/{chainflipAssetMap.Usdc.symbol}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
            <PeriodSelector
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              selectedDates={selectedDates}
              setSelectedDates={setSelectedDates}
            />
            <Button type="primary-standard" size="small">
              <Link
                className="flex items-center gap-x-1"
                href={`${process.env.NEXT_PUBLIC_SWAPPING_URL}?srcAsset=Usdc&destAsset=${baseAsset}`}
                target="_blank"
              >
                <span>
                  Swap {chainflipAssetMap[baseAsset].symbol}/{chainflipAssetMap.Usdc.symbol}
                </span>
                <ArrowIcon direction="right" />
              </Link>
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-x-[24px] gap-y-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-y-8">
          <PoolCards
            startDate={startDate}
            endDate={endDate}
            baseAsset={baseAsset}
            openOrders={openOrders}
            openOrdersLoading={cacheDataLoading || poolDataLoading || assetPricesLoading}
          />
        </div>
        <div className="flex flex-1 flex-grow flex-col justify-center space-y-6 rounded-lg border border-cf-gray-4 bg-cf-gray-2 p-6">
          {!marketDataLoading && (
            <div className="flex flex-col">
              <span className="text-30 text-white">${preciseUsdPrice}</span>
              {diff && percentDiff && (
                <div className="text-12">
                  {diff.isZero() ? (
                    <span className="text-cf-light-2">
                      {diff.isPositive() && '+'}
                      {diff.toFixed(2)} ({percentDiff}%)
                    </span>
                  ) : (
                    <span className={diff.isPositive() ? 'text-cf-green-3' : 'text-cf-red-2'}>
                      {diff.isPositive() && '+'}
                      {diff.toFixed(2)} ({percentDiff}%)
                    </span>
                  )}
                  <span className="text-cf-light-2">
                    &nbsp;/&nbsp;
                    {getTimeframeOrFormattedDateLabels([startDate, currDate as UTCDate])}
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="h-[350px]">
            <TVChartContainer
              asset={baseAsset as BaseChainflipAsset}
              timeframe={`${utcDaysDifference}D` as ResolutionString}
              interval={(utcDaysDifference >= 30 ? '1D' : '1H') as ResolutionString}
              disabledFeatures={['left_toolbar', 'header_indicators']}
            />
          </div>
        </div>
        <TabPanel
          queryParamName="tab"
          onChange={(nodeName) => {
            track(BlockExplorerEvents.SwitchTabPools, {
              props: {
                path: window.location.pathname,
                pool: baseAsset,
                tab: nodeName,
              },
            });
          }}
          nodes={[
            {
              name: 'Open Orders',
              content: <RpcOrdersTable orders={openOrders} displayLp />,
            },
            {
              name: 'Swaps',
              content: (
                <TableV2
                  columns={[
                    'Swap Id',
                    'Swap Details',
                    'Status',
                    'Price',
                    { name: 'Age', alignment: 'right' },
                  ]}
                  emptyRowsContent="No swaps here yet"
                  isLoading={swapLoading}
                  rows={swapRows}
                  paginationControls={swapPagination}
                />
              ),
              contentRight: (
                <div className="mr-5 hidden text-12 text-cf-light-3 sm:block">
                  <Toggle
                    value={hideInternalSwaps}
                    onToggle={() => setHideInternalSwaps(!hideInternalSwaps)}
                    label={{ content: 'Hide system-initiated swaps', side: 'right' }}
                  />
                </div>
              ),
            },
            {
              name: 'Order Fills',
              content: (
                <OrderFillsTable hideSmallAmounts={hideSmallOrderFillAmounts} asset={baseAsset} />
              ),
              contentRight: (
                <div className="mr-5 hidden text-12 text-cf-light-3 sm:block">
                  <Toggle
                    value={hideSmallOrderFillAmounts}
                    onToggle={() => setHideSmallOrderFillAmounts(!hideSmallOrderFillAmounts)}
                    label={{ content: 'Hide small amounts', side: 'right' }}
                  />
                </div>
              ),
            },
          ]}
        />
      </div>
    </Layout>
  );
}
